import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, Video, Lock, Users, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Loading, EmptyState, TierBadge } from "@/components/shared";
import api from "@/lib/api";

export default function LiveSessions() {
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useQuery({ queryKey: ["sessions"], queryFn: async () => (await api.get("/sessions")).data });

  const register = async (id) => {
    try { await api.post(`/sessions/${id}/register`); toast.success("You're registered!"); refetch(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upgrade to register"); }
  };

  return (
    <DashboardLayout>
      <div className="mb-8"><h1 className="font-heading text-3xl font-bold tracking-tight text-navy">CEO Playbook Live</h1><p className="mt-1 text-slate-600">Live teaching and Q&A with Shubham Bapna.</p></div>
      {isLoading ? <Loading /> : (data?.length ? (
        <div className="grid gap-5 md:grid-cols-2">
          {data.map((s) => (
            <div key={s.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`session-${s.id}`}>
              <div className="relative aspect-video bg-navy">{s.cover_image && <img src={s.cover_image} alt="" className="h-full w-full object-cover opacity-80" />}<div className="absolute left-3 top-3"><TierBadge tier={s.membership_requirement} /></div></div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-semibold text-navy">{s.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{s.description}</p>
                <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {new Date(s.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                  <span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> {s.registrant_count} registered</span>
                </div>
                <div className="mt-4">
                  {!s.eligible ? (
                    <Button onClick={() => navigate("/pricing")} variant="outline" className="w-full rounded-md" data-testid={`session-upgrade-${s.id}`}><Lock className="mr-1.5 h-4 w-4" /> Upgrade to attend</Button>
                  ) : s.registered ? (
                    <div>
                      <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-emerald"><CheckCircle2 className="h-4 w-4" /> Registered</div>
                      {s.meeting_link && <a href={s.meeting_link} target="_blank" rel="noreferrer"><Button className="w-full rounded-md bg-navy text-white hover:bg-navysurface" data-testid={`session-join-${s.id}`}><Video className="mr-1.5 h-4 w-4" /> Join link</Button></a>}
                    </div>
                  ) : (
                    <Button onClick={() => register(s.id)} className="w-full rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid={`session-register-${s.id}`}>Register</Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : <EmptyState title="No sessions scheduled" />)}
    </DashboardLayout>
  );
}
